import { useCallback, useMemo } from 'react'

import type { OwnStore } from '../../../state'
import type { Cart } from '../../sale/model/cart'
import type { ReceiptRequestDraft } from '../../sale/model/draft'
import type { OwnSnapshotPort } from './snapshotPort'
import { snapshotMatches, takeSnapshot } from './snapshot'
import { ownSaleSnapshotCleared, ownSaleSnapshotStarted, snapshotOf } from './snapshotSlice'

/**
 * Порт снимка строк чека для продажи «Собственной» (RP-OWN4): состояние лежит в слайсе модуля `own` с сроком `session` (реестр
 * хранения), поэтому переживает F5. Хук только связывает порт со store; правила снимка — в `snapshot.ts`.
 */
export function useOwnSnapshot(resourceId: string, store: OwnStore): OwnSnapshotPort {
  const { dispatch } = store
  const current = snapshotOf(store.state.saleSnapshots, resourceId)
  const clear = useCallback((): void => {
    dispatch(ownSaleSnapshotCleared({ resourceId }))
  }, [dispatch, resourceId])
  const begin = useCallback(
    (input: { cart: Cart; draft: ReceiptRequestDraft; paymentKind: 'Cash' | 'Card'; totalKopecks: number }): ReceiptRequestDraft => {
      // Та же продажа, что уже начата, — повтор: берём снимок, а не заново собранный запрос (карточка могла измениться).
      if (current !== null && snapshotMatches(current, input.cart, input.paymentKind, input.totalKopecks)) return current.draft
      const snapshot = takeSnapshot({ ...input, startedAt: new Date().toISOString() })
      if (snapshot === null) return input.draft
      dispatch(ownSaleSnapshotCleared({ resourceId }))
      dispatch(ownSaleSnapshotStarted({ resourceId, snapshot }))
      return snapshot.draft
    },
    [current, dispatch, resourceId],
  )
  return useMemo(() => ({ current, begin, clear }), [begin, clear, current])
}
